import React, { useEffect, useMemo, useState } from "react";
import { DateInput } from "../components/DateInput";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { toast, Toaster } from "sonner";
import { ArrowRight, CheckCircle2, FilePlus2, Save, Pencil, Send, Printer, Download, FileSearch } from "lucide-react";
import Header from "../components/Header";
import ModuleHero from "../components/ModuleHero";
import { Users as UsersIcon } from "lucide-react";
import StepTabs from "../components/StepTabs";
import TopActionBar from "../components/TopActionBar";
import HomeNavBar from "../components/HomeNavBar";
import { parseGstin } from "../lib/gstin";
import VendorQueryModal from "../components/VendorQueryModal";
import {
  createVendorDraft, upsertVendor, findVendorByAny, advanceVendorStep,
  STEP_LABEL, currentUser, exportJSON, generateTempRefId, getVendors,
  canFinalApprove, currentUserEmail, APPROVER_EMAIL, APPROVER_NAME, fmtDMY,
  canCreate, canApprove, deleteVendor, isSuperAdmin,
} from "../lib/store";

const ACCOUNT_TYPES = ["Current Account", "Savings Account", "Overdraft (OD)", "Cash Credit (CC)"];

const F = ({ label, req, children, test }) => (
  <div className="field-b" data-testid={test ? `wrap-${test}` : undefined}>
    <label>{label}{req && <span className="req"> *</span>}</label>
    {children}
  </div>
);

export default function VendorRegistration({ embedded = false }) {
  const nav = useNavigate();
  const [vendor, setVendor] = useState(() => createVendorDraft());
  const [locked, setLocked] = useState(false);
  const [queryOpen, setQueryOpen] = useState(false);
  const [queryCode, setQueryCode] = useState("");
  const [refresh, setRefresh] = useState(0);

  // REAL-TIME CROSS-USER SYNC: re-render on ae-sync-done (every ~8s via App.js).
  useEffect(() => {
    const onSync = () => setRefresh((v) => v + 1);
    window.addEventListener("ae-sync-done", onSync);
    return () => window.removeEventListener("ae-sync-done", onSync);
  }, []);

  const allVendors = useMemo(() => getVendors(), [refresh, vendor.id, vendor.step]);
  const loadVendorById = (v) => {
    // Normalize legacy vendors that may have missing/invalid step
    const normalized = { ...v, step: [1, 2, 3].includes(v.step) ? v.step : 1 };
    setVendor(normalized);
    setLocked(normalized.step === 3);
    if (normalized.step !== v.step) upsertVendor(normalized); // persist fix
    toast.success(`Loaded ${normalized.firmName || normalized.tempRefId} · ${normalized.vendorCode || normalized.tempRefId}`);
  };

  useEffect(() => {
    setVendor((v) => ({ ...v, createdByName: v.createdByName || currentUser() }));
  }, []);

  const bind = (k, extra = {}) => ({
    value: vendor[k] ?? "",
    onChange: (e) => setVendor({ ...vendor, [k]: e.target.value }),
    readOnly: locked,
    "data-testid": `field-${k}`,
    ...extra,
  });

  // ---- IFSC → auto-fetch bank details (Razorpay's free public API) ----
  const [ifscBusy, setIfscBusy] = useState(false);
  const [ifscOk, setIfscOk] = useState(false);
  useEffect(() => {
    const code = (vendor.ifscCode || "").toUpperCase().trim();
    if (code.length !== 11 || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(code)) {
      setIfscOk(false);
      return;
    }
    let cancelled = false;
    setIfscBusy(true);
    fetch(`https://ifsc.razorpay.com/${code}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        setVendor((prev) => ({
          ...prev,
          bankName: data.BANK || prev.bankName,
          branchName: data.BRANCH || prev.branchName,
          bankAddress: data.ADDRESS || prev.bankAddress,
          bankCity: data.CITY || prev.bankCity,
          bankState: data.STATE || prev.bankState,
        }));
        setIfscOk(true);
        toast.success(`Bank: ${data.BANK} · ${data.BRANCH}`);
      })
      .catch(() => {
        if (cancelled) return;
        setIfscOk(false);
        toast.error(`IFSC "${code}" not found`);
      })
      .finally(() => { if (!cancelled) setIfscBusy(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor.ifscCode]);

  const gstInfo = parseGstin(vendor.gstNo);
  useEffect(() => {
    if (!gstInfo.valid || locked) return;
    setVendor((p) => ({ ...p, panNo: p.panNo || gstInfo.pan, state: p.state || gstInfo.state }));
    toast.success(`GSTIN valid · ${gstInfo.state} · PAN ${gstInfo.pan}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstInfo.valid, vendor.gstNo]);

  const validateStep1 = () => {
    if (!String(vendor.firmName || "").trim()) { toast.error("Vendor / Firm Name is required"); return false; }
    if (!String(vendor.mobileNo || "").trim()) { toast.error("Mobile Number is required"); return false; }
    return true;
  };

  const handleNew = () => {
    setVendor({ ...createVendorDraft(), createdByName: currentUser() });
    setLocked(false);
    toast.success("New vendor draft created");
  };
  const handleSave = () => {
    if (!validateStep1()) return;
    // Temp Ref ID is issued at first Save of Step 1 (Creation start)
    const withTemp = vendor.tempRefId
      ? { ...vendor }
      : { ...vendor, tempRefId: generateTempRefId() };
    const saved = upsertVendor(withTemp);
    setVendor(saved);
    toast.success(`Saved (${saved.tempRefId})`);
  };
  const handleEdit = () => {
    if (vendor.step === 3) {
      if (!window.confirm(`Reopen approved vendor ${vendor.vendorCode} for editing? This requires Re-Approval.`)) return;
      const history = Array.isArray(vendor.history) ? vendor.history : [];
      history.push({
        action: "Reopened",
        from: "Approved",
        by: currentUser(),
        at: new Date().toISOString(),
      });
      const next = {
        ...vendor,
        step: 2,
        revision: (vendor.revision || 0) + 1,
        reopenedAt: new Date().toISOString(),
        reopenedBy: currentUser(),
        history,
      };
      setVendor(upsertVendor(next));
      setLocked(false);
      toast.success(`${vendor.vendorCode} reopened (Rev ${next.revision}) · Awaiting Re-Approval`);
      return;
    }
    setLocked(false);
    toast.info("You can edit the form");
  };
  const handleSubmit = () => {
    const curStep = [1, 2, 3].includes(vendor.step) ? vendor.step : 1;
    const isReSubmit = curStep === 2 && (vendor.revision || 0) > 0;
    if (curStep === 3) { toast.error("Already approved — click Edit to reopen for changes"); return; }
    if (!validateStep1()) return;
    if (!vendor.tempRefId) { toast.error("Please Save the record first — Temp Ref ID is required"); return; }
    if (!(vendor.checkedByName || "").trim()) { toast.error("Enter 'Checked By (Name)' in Step 2 section"); return; }
    if (isReSubmit) {
      // Re-Submit after reopen — record is at step 2 with revision > 0, awaiting Re-Approval.
      const history = Array.isArray(vendor.history) ? vendor.history : [];
      history.push({ action: "Re-Submitted", by: currentUser(), at: new Date().toISOString() });
      const saved = upsertVendor({ ...vendor, history, submittedAt: new Date().toISOString() });
      setVendor(saved);
      toast.success(`Re-Submitted (Rev ${vendor.revision}) · Awaiting Re-Approval`);
      return;
    }
    if (curStep !== 1) { toast.error("Already submitted for checking"); return; }
    const next = advanceVendorStep({ ...vendor, step: curStep });
    setVendor(next);
    toast.success("Submitted for Checking & Verification");
  };
  const handleApprove = () => {
    const curStep = [1, 2, 3].includes(vendor.step) ? vendor.step : 1;
    if (curStep === 3) { toast.info("Already approved"); return; }
    if (curStep === 1) { toast.error("Please Submit for Checking first"); return; }
    if (!(vendor.approvedByName || "").trim()) { toast.error("Enter 'Approved By' name in Step 3 section"); return; }
    const isReApproval = (vendor.revision || 0) > 0;
    const history = Array.isArray(vendor.history) ? vendor.history : [];
    history.push({
      action: isReApproval ? "Re-Approved" : "Approved",
      by: currentUser(),
      at: new Date().toISOString(),
    });
    const next = { ...advanceVendorStep({ ...vendor, step: curStep }), history };
    setVendor(upsertVendor(next));
    setLocked(true);
    toast.success(
      isReApproval
        ? `Re-Approved (Rev ${next.revision}) · Vendor Code: ${next.vendorCode}`
        : `Final Approval done · Vendor Code: ${next.vendorCode}`
    );
  };
  const handleSearch = (q) => {
    const v = findVendorByAny(q);
    if (v) {
      setVendor(v);
      setLocked(v.step === 3);
      toast.success(`Loaded ${v.firmName || v.tempRefId} · ${v.vendorCode || v.tempRefId}`);
      return true;
    }
    toast.error("Vendor not found · Opening Query lookup");
    return false;
  };
  const openQuery = (q) => { setQueryCode(q || ""); setQueryOpen(true); };
  const handleExportJSON = () => {
    exportJSON(vendor, `vendor_${vendor.vendorCode || vendor.tempRefId || "draft"}.json`);
    toast.success("Vendor exported as JSON");
  };
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const rows = Object.entries(vendor).map(([k, v]) => ({ Field: k, Value: v }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Vendor");
    XLSX.writeFile(wb, `vendor_${vendor.vendorCode || vendor.tempRefId || "draft"}.xlsx`);
    toast.success("Vendor exported as Excel");
  };
  const handlePrint = () => window.print();

  return (
    <>
      {!embedded && <><Toaster position="top-center" richColors />
      <HomeNavBar /></>}
      <div className={embedded ? "" : "app-shell"}>
        <TopActionBar
          searchPlaceholder="Vendor Code / Temp ID / Firm Name / Mobile"
          searchLabel="Search"
          onSearch={handleSearch}
          onQuery={openQuery}
          nextLabel="Bill Process"
          nextTo="/bill"
          onNew={handleNew}
          onSave={handleSave}
          onEdit={handleEdit}
          onSubmit={handleSubmit}
          onApprove={handleApprove}
          onPrint={handlePrint}
          onExportJSON={handleExportJSON}
          onExportExcel={handleExportExcel}
          disabled={{ submit: vendor.step === 3, approve: vendor.step === 3 && !((vendor.revision || 0) > 0) }}
        />
        <VendorQueryModal
          open={queryOpen}
          onClose={() => setQueryOpen(false)}
          initialCode={queryCode}
          variant="vendor"
          onLoad={(v) => { setVendor(v); setLocked(v.step === 3); toast.success(`Loaded ${v.firmName || v.tempRefId}`); }}
        />

        <div className={embedded ? "" : "a4"} data-testid="vendor-page">
          {!embedded && <>
          <div className="module-doc-header"><Header formTitle="VENDOR / SUPPLIER REGISTRATION FORM" /></div>
          <ModuleHero
            tag="Procurement Management"
            title="Registration · KYC · Approval"
            subtitle="3-step workflow · Auto Vendor Code AE####JV{FY}"
            icon={UsersIcon}
            gradient="linear-gradient(135deg,#075985 0%,#0369a1 45%,#06b6d4 100%)"
            shadowRgba="3,105,161,.28"
          />
          </>}
          <StepTabs step={vendor.step} />

          {vendor.step === 3 && vendor.vendorCode && (
            <div className="approval-banner" data-testid="approval-banner">
              <CheckCircle2 size={18} />
              <span>VENDOR APPROVED · PERMANENT VENDOR CODE:</span>
              <span className="code" data-testid="approved-vendor-code">{vendor.vendorCode}</span>
              {vendor.revision > 0 && (
                <span
                  data-testid="vendor-rev-badge"
                  style={{ padding: "2px 8px", fontSize: 10, background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", borderRadius: 999, fontWeight: 700, marginLeft: 8 }}
                  title={`Reopened & Re-Approved ${vendor.revision} time(s)`}
                >
                  REV {vendor.revision}
                </span>
              )}
              <span className="exp">VALID TILL: {fmtDMY(vendor.expiryDate)}</span>
            </div>
          )}

          <div className="section">
            <div className="sec-head">System Reference &amp; Vendor / Supplier Code</div>
            <div className="sec-body">
              <div className="row cols-3">
                <F label="Temporary Ref ID (Active till Approval)">
                  <input className="pill" data-testid="field-tempRefId"
                    value={vendor.tempRefId || "Generated on First Save"} readOnly />
                </F>
                <F label="Permanent Vendor / Supplier Code">
                  <input className="pill-generated" data-testid="field-vendorCode"
                    value={vendor.vendorCode || "Generated on Step 3 Approval"} readOnly />
                </F>
                <F label="Current Status">
                  <input className="pill-status" data-testid="field-currentStatus" value={STEP_LABEL[vendor.step]} readOnly />
                </F>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="sec-head">Project &amp; Site Details</div>
            <div className="sec-body">
              <div className="row cols-2">
                <F label="Project Code"><input placeholder="e.g. PRJ-2026-NNA" {...bind("projectCode")} /></F>
                <F label="Project Location / Site Name"><input placeholder="e.g. Naini Site / Prayagraj" {...bind("siteName")} /></F>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="sec-head">1. Vendor Basic, Contact &amp; Address Details</div>
            <div className="sec-body">
              <div className="row cols-3">
                <F label="Vendor Type" req>
                  <select {...bind("vendorType")} data-testid="field-vendorType">
                    <option value="Vendor">Vendor</option>
                    <option value="Supplier">Supplier</option>
                  </select>
                </F>
                <F label="Vendor / Firm Name" req><input placeholder="Full Firm Name" {...bind("firmName")} /></F>
                <F label="Contact Person Name"><input placeholder="Contact Person Name" {...bind("contactPerson")} /></F>
              </div>
              <div className="row cols-3" style={{ marginTop: 10 }}>
                <F label="Mobile Number" req><input placeholder="Phone Number" maxLength={12} {...bind("mobileNo")} /></F>
                <F label="Vendor Email ID (For Work Orders)"><input type="email" placeholder="vendor@domain.com" {...bind("email")} /></F>
                <F label={`GST Number${gstInfo.valid ? ` · ${gstInfo.entityType}` : ""}`}>
                  <div style={{ position: "relative" }}>
                    <input placeholder="GSTIN (auto-fills PAN & State)" maxLength={15}
                      {...bind("gstNo", { style: { textTransform: "uppercase", paddingRight: 40, ...(gstInfo.valid ? { background: "#f0fdf4" } : {}) } })} />
                    <span data-testid="gst-status" style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: (vendor.gstNo || "").length === 15 ? "inline-block" : "none", fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: gstInfo.valid ? "#dcfce7" : "#fee2e2", color: gstInfo.valid ? "#166534" : "#991b1b" }}>{gstInfo.valid ? "✓" : "✗"}</span>
                  </div>
                </F>
              </div>
              <div className="row cols-2" style={{ marginTop: 10 }}>
                <F label="PAN Number">
                  <input placeholder="PAN Number" maxLength={10}
                    {...bind("panNo", { style: { textTransform: "uppercase" } })} />
                </F>
                <F label="Registered Street Address"><input placeholder="Premises No, Street, Area" {...bind("streetAddress")} /></F>
              </div>
              <div className="row cols-3" style={{ marginTop: 10 }}>
                <F label="City"><input placeholder="City" {...bind("city")} /></F>
                <F label="State"><input placeholder="State" {...bind("state")} /></F>
                <F label="Pincode / PIN Number"><input placeholder="PIN Code" maxLength={6} {...bind("pincode")} /></F>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="sec-head">2. Vendor Bank Details</div>
            <div className="sec-body">
              <div className="row cols-3">
                <F label="IFSC Code (Primary — auto-fills bank details)" req>
                  <div style={{ position: "relative" }}>
                    <input placeholder="IFSC CODE (E.G. SBIN0001234)" maxLength={11}
                      {...bind("ifscCode", { style: { textTransform: "uppercase", paddingRight: 60 } })} />
                    <span style={{
                      position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                      fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                      background: ifscBusy ? "#fef3c7" : ifscOk ? "#dcfce7" : "#fee2e2",
                      color: ifscBusy ? "#92400e" : ifscOk ? "#166534" : "#991b1b",
                      border: `1px solid ${ifscBusy ? "#fcd34d" : ifscOk ? "#86efac" : "#fca5a5"}`,
                      display: (vendor.ifscCode || "").length === 11 ? "inline-block" : "none",
                    }} data-testid="ifsc-status">
                      {ifscBusy ? "…" : ifscOk ? "✓" : "✗"}
                    </span>
                  </div>
                </F>
                <F label="Bank Name (auto)">
                  <input placeholder="Bank Name" {...bind("bankName")} readOnly={locked || ifscOk} style={ifscOk ? { background: "#f0fdf4" } : {}} />
                </F>
                <F label="Branch Name (auto)">
                  <input placeholder="Branch Name / City" {...bind("branchName")} readOnly={locked || ifscOk} style={ifscOk ? { background: "#f0fdf4" } : {}} />
                </F>
              </div>
              <div className="row cols-1" style={{ marginTop: 10 }}>
                <F label="Bank Branch Address (auto)">
                  <input placeholder="Auto-filled from IFSC lookup" {...bind("bankAddress")} readOnly={locked || ifscOk} style={ifscOk ? { background: "#f0fdf4" } : {}} data-testid="field-bankAddress" />
                </F>
              </div>
              <div className="row cols-3" style={{ marginTop: 10 }}>
                <F label="Bank City (auto)">
                  <input placeholder="City" {...bind("bankCity")} readOnly={locked || ifscOk} style={ifscOk ? { background: "#f0fdf4" } : {}} data-testid="field-bankCity" />
                </F>
                <F label="Bank State (auto)">
                  <input placeholder="State" {...bind("bankState")} readOnly={locked || ifscOk} style={ifscOk ? { background: "#f0fdf4" } : {}} data-testid="field-bankState" />
                </F>
                <F label="Account Type">
                  <select {...bind("accountType")}>
                    {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </F>
              </div>
              <div className="row cols-2" style={{ marginTop: 10 }}>
                <F label="Account Holder Name"><input placeholder="Name as per Bank" {...bind("accountHolder")} /></F>
                <F label="Account Number"><input placeholder="Bank Account Number" {...bind("accountNumber")} /></F>
              </div>
            </div>
          </div>

          <div className="section">
            <div className="sec-head">3. Workflow &amp; Approvals</div>
            <div className="sec-body">
              <div className="substep">
                <div className="substep-title">[STEP 1] CREATED BY</div>
                <div className="row cols-2">
                  <F label="Created By (Fill Name)"><input placeholder="Name of Creator" {...bind("createdByName")} /></F>
                  <F label="Creation Date"><DateInput type="date" {...bind("creationDate")} /></F>
                </div>
              </div>
              <div className="substep">
                <div className="substep-title">[STEP 2] CHECKED &amp; PREPARED BY</div>
                <div className="row cols-2">
                  <F label="Checked By (Name)"><input placeholder="Checker Name" {...bind("checkedByName")} /></F>
                  <F label="Prepared By (Name with Purchase Manager)"><input placeholder="e.g. Divya Srivastava (Purchase Manager)" {...bind("preparedByName")} /></F>
                </div>
              </div>
              <div className="substep">
                <div className="substep-title">[STEP 3] FINAL APPROVAL &amp; EXPIRY</div>
                <div className="row cols-3">
                  <F label="Approved By (Name with Account Manager)"><input placeholder="e.g. Komal Srivastava (Account Manager)" {...bind("approvedByName")} /></F>
                  <F label="Date of Approval"><input className="date-highlight" value={fmtDMY(vendor.approvalDate) || ""} readOnly placeholder="dd / mm / yyyy" /></F>
                  <F label="Expiry Date (2 Years Valid)"><input className="date-highlight" value={fmtDMY(vendor.expiryDate) || ""} readOnly placeholder="dd / mm / yyyy" /></F>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>

          {/* Vendors Data Sheet — bottom of page, all vendors listed */}
          {allVendors.length > 0 && (
            <div className="section" data-testid="vendors-data-sheet" style={{ marginTop: 14 }}>
              <div className="sec-head">Vendors Data Sheet · Click to Load (⏳ Temp ID till Approval · ✓ Vendor Code after)</div>
              <div className="sec-body">
                <table className="data-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: "#f1f5f9" }}>
                      <th style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Code / Temp ID</th>
                      <th style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Vendor / Supplier Name</th>
                      <th style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Type</th>
                      <th style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Mobile</th>
                      <th style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>GST No</th>
                      <th style={{ padding: "6px 8px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>PAN</th>
                      <th style={{ padding: "6px 8px", textAlign: "center", borderBottom: "1px solid #cbd5e1" }}>Status</th>
                      <th style={{ padding: "6px 8px", textAlign: "center", borderBottom: "1px solid #cbd5e1" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allVendors.map((v) => {
                      const stepN = [1, 2, 3].includes(v.step) ? v.step : 1;
                      const isApp = stepN === 3;
                      const isActive = vendor.id === v.id;
                      return (
                        <tr key={v.id} data-testid={`vendor-row-${v.id}`}
                          style={{ background: isActive ? "#eef4ff" : "#fff", borderBottom: "1px solid #e2e8f0" }}>
                          <td style={{ padding: "5px 8px", fontFamily: "monospace", fontWeight: 700, color: isApp ? "#166534" : "#92400e" }}>
                            {isApp ? `✓ ${v.vendorCode}` : `⏳ ${v.tempRefId || "—"}`}
                            {v.revision > 0 && <span style={{ marginLeft: 6, padding: "1px 6px", background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d", borderRadius: 999, fontSize: 9, fontWeight: 700 }}>R{v.revision}</span>}
                          </td>
                          <td style={{ padding: "5px 8px" }}>{v.firmName || "—"}</td>
                          <td style={{ padding: "5px 8px" }}>{v.vendorType || "Vendor"}</td>
                          <td style={{ padding: "5px 8px" }}>{v.mobileNo || "—"}</td>
                          <td style={{ padding: "5px 8px", fontFamily: "monospace" }}>{v.gstNo || "—"}</td>
                          <td style={{ padding: "5px 8px", fontFamily: "monospace" }}>{v.panNo || "—"}</td>
                          <td style={{ padding: "5px 8px", textAlign: "center" }}>
                            <span style={{ padding: "2px 8px", fontSize: 10, fontWeight: 700, borderRadius: 999, background: isApp ? "#dcfce7" : "#fef3c7", color: isApp ? "#166534" : "#92400e", border: `1px solid ${isApp ? "#86efac" : "#fcd34d"}` }}>
                              {isApp ? "Approved" : `Step ${stepN}`}
                            </span>
                          </td>
                          <td style={{ padding: "5px 8px", textAlign: "center" }}>
                            <button
                              onClick={() => loadVendorById(v)}
                              data-testid={`vendor-load-${v.id}`}
                              style={{ padding: "3px 10px", fontSize: 10.5, background: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}
                            >
                              Load
                            </button>
                            {isSuperAdmin(currentUserEmail()) && (
                              <button
                                onClick={() => {
                                  const label = v.vendorCode || v.tempRefId || v.firmName;
                                  if (!window.confirm(`Vendor "${v.firmName || ""}" (${label}) ko pakka delete karein? Yeh wapas nahi aayega.`)) return;
                                  deleteVendor(v.id);
                                  if (vendor.id === v.id) { setVendor(createVendorDraft()); setLocked(false); }
                                  setRefresh((x) => x + 1);
                                  toast.success(`Vendor ${label} deleted`);
                                }}
                                data-testid={`vendor-sa-del-${v.id}`}
                                title="SuperAdmin: Delete vendor (Temp ya Permanent)"
                                style={{ marginLeft: 4, padding: "3px 8px", fontSize: 10.5, background: "#fee2e2", color: "#b91c1c", border: "1px solid #fca5a5", borderRadius: 4, cursor: "pointer", fontWeight: 700 }}
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Inline Action Buttons — uniform left-to-right alignment */}
          <div style={{ order: -1, marginBottom: 12, display: "flex", justifyContent: "flex-start", alignItems: "stretch", gap: 6, flexWrap: "wrap", padding: "10px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6 }} data-testid="vendor-action-row">
            <button data-testid="vendor-btn-query" onClick={() => openQuery()} style={{ padding: "7px 12px", height: 32, background: "#0891b2", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, lineHeight: 1 }}><FileSearch size={13} /> Query</button>
            <button data-testid="vendor-btn-new" onClick={handleNew} disabled={!canCreate()} style={{ padding: "7px 12px", height: 32, background: canCreate() ? "#0369a1" : "#94a3b8", color: "#fff", border: "none", borderRadius: 5, cursor: canCreate() ? "pointer" : "not-allowed", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, lineHeight: 1 }}><FilePlus2 size={13} /> New Vendor</button>
            <button data-testid="vendor-btn-edit" onClick={handleEdit} disabled={!canCreate()} style={{ padding: "7px 12px", height: 32, background: "#eab308", color: "#fff", border: "none", borderRadius: 5, cursor: canCreate() ? "pointer" : "not-allowed", opacity: canCreate() ? 1 : .5, fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, lineHeight: 1 }}><Pencil size={13} /> Edit</button>
            <button data-testid="vendor-btn-save" onClick={handleSave} disabled={!canCreate()} style={{ padding: "7px 12px", height: 32, background: "#16a34a", color: "#fff", border: "none", borderRadius: 5, cursor: canCreate() ? "pointer" : "not-allowed", opacity: canCreate() ? 1 : .5, fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, lineHeight: 1 }}><Save size={13} /> Save</button>
            <button data-testid="vendor-btn-submit" onClick={handleSubmit} disabled={!canCreate() || vendor.step === 3} style={{ padding: "7px 12px", height: 32, background: vendor.step === 3 ? "#94a3b8" : "#7c3aed", color: "#fff", border: "none", borderRadius: 5, cursor: canCreate() && vendor.step !== 3 ? "pointer" : "not-allowed", opacity: canCreate() && vendor.step !== 3 ? 1 : .5, fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, lineHeight: 1 }}><Send size={13} /> Submit</button>
            <button data-testid="vendor-btn-approve" onClick={handleApprove} disabled={!canApprove() || (vendor.step === 3 && !((vendor.revision || 0) > 0))} style={{ padding: "7px 12px", height: 32, background: canApprove() && !(vendor.step === 3 && !((vendor.revision || 0) > 0)) ? "#dc2626" : "#94a3b8", color: "#fff", border: "none", borderRadius: 5, cursor: canApprove() && !(vendor.step === 3 && !((vendor.revision || 0) > 0)) ? "pointer" : "not-allowed", opacity: canApprove() && !(vendor.step === 3 && !((vendor.revision || 0) > 0)) ? 1 : .5, fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, lineHeight: 1 }}><CheckCircle2 size={13} /> Final Approval</button>
            <span style={{ width: 1, height: 24, background: "#cbd5e1", margin: "4px 6px", alignSelf: "center" }} />
            <span style={{ fontSize: 12, fontWeight: 800, color: "#fff", letterSpacing: 1, textTransform: "uppercase", padding: "7px 12px", height: 32, background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)", borderRadius: 5, boxShadow: "0 1px 3px rgba(79,70,229,.35)", display: "inline-flex", alignItems: "center", lineHeight: 1, boxSizing: "border-box" }}>Export</span>
            <button data-testid="vendor-btn-export-json" onClick={handleExportJSON} style={{ padding: "7px 12px", height: 32, background: "#2563eb", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, boxShadow: "0 1px 3px rgba(37,99,235,.3)", lineHeight: 1 }}><Download size={13} /> JSON</button>
            <button data-testid="vendor-btn-export-excel" onClick={handleExportExcel} style={{ padding: "7px 12px", height: 32, background: "#059669", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, boxShadow: "0 1px 3px rgba(5,150,105,.3)", lineHeight: 1 }}>⬇ Excel</button>
            <button data-testid="vendor-btn-print" onClick={handlePrint} style={{ padding: "7px 12px", height: 32, background: "#ea580c", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontWeight: 700, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, boxShadow: "0 1px 3px rgba(234,88,12,.3)", lineHeight: 1 }}><Printer size={13} /> Print</button>
          </div>
          </div>

        </div>
      </div>
    </>
  );
}
